import { logger } from '../utils/logger/logger';
import { VerificationStep, VerificationResponse, MockStep, MilitaryStatus, FirstResponderStatus, SubSegment, ErrorId } from '../types/types';
import { MockStepsEnum, VerificationStepsEnum } from '../types/runtimeTypes';
import { getOptions } from '../../options/options';
import { QUERY_STRING_STEP_OVERRIDE, QUERY_STRING_SUBSEGMENT_OVERRIDE, QUERY_STRING_ERRORID_OVERRIDE } from '../../constants';

import {
    mockStudentPersonalInfoResponse,
    mockTeacherPersonalInfoResponse,
    mockDocUploadVerificationResponse,
    mockSuccessResponse,
    mockErrorResponse,
    mockPendingResponse,
    mockDocReviewLimitExceededResponse,
    mockActiveMilitaryPersonalInfoResponse,
    mockInactiveMilitaryPersonalInfoResponse,
    mockMilitaryStatusResponse,
    mockSeniorPersonalInfoResponse,
    mockFirstResponderPersonalInfoResponse,
    mockEmploymentPersonalInfoResponse,
    mockSSOResponse,
} from '../../serverMocks/mocks';

// To load a specific step directly, visit e.g. localhost:1234/?step=success
export const getRouteOverride = (): MockStep => {
    try {
        const parsed: URLSearchParams = new URLSearchParams(window.location.search);

        if (parsed.get(QUERY_STRING_STEP_OVERRIDE)) {
            return <VerificationStep> parsed.get(QUERY_STRING_STEP_OVERRIDE);
        }
        if (getOptions().mockStep) {
            return getOptions().mockStep;
        }
        return null;
    } catch (e) {
        logger.error(e);
    }
};

// When loading a specific step, specify a subsegment
export const getSubsegmentOverride = (): SubSegment => {
    try {
        const parsed: URLSearchParams = new URLSearchParams(window.location.search);

        if (parsed.get(QUERY_STRING_SUBSEGMENT_OVERRIDE)) {
            return <SubSegment> parsed.get(QUERY_STRING_SUBSEGMENT_OVERRIDE);
        }
        if (getOptions().mockSubSegment) {
            return getOptions().mockSubSegment;
        }
        return null;
    } catch (e) {
        logger.error(e);
    }
};

// When loading the error step, specify an errorId
export const getErrorIdOverride = (): ErrorId => {
    try {
        const parsed: URLSearchParams = new URLSearchParams(window.location.search);

        if (parsed.get(QUERY_STRING_ERRORID_OVERRIDE)) {
            return <ErrorId> parsed.get(QUERY_STRING_ERRORID_OVERRIDE);
        }
        if (getOptions().mockErrorId) {
            return getOptions().mockErrorId;
        }
        return null;
    } catch (e) {
        logger.error(e);
    }
};

// Special case. The loading screen is not a step, so mocking it is a little different.
export const isMockingLoading = (): boolean => {
    try {
        const parsed: URLSearchParams = new URLSearchParams(window.location.search);
        return (parsed.get(QUERY_STRING_STEP_OVERRIDE) === MockStepsEnum.loading || getOptions().mockStep === MockStepsEnum.loading);
    } catch (e) {
        logger.error(e);
    }
};

/**
 * Based on page request query params, return a fake response to allow direct access to any step.
 *
 * @param overriddenStep
 * @param serverResponse The actual serverResponse to acquire certain pieces of info from.
 */
export const getOverriddenMock = async (overriddenStep: MockStep, serverResponse?: VerificationResponse): Promise<VerificationResponse> => {
    let mockResponse;
    const availableMilitaryStatuses: MilitaryStatus[] = ['ACTIVE_DUTY', 'VETERAN'];
    const availableFirstResponderStatuses: FirstResponderStatus[] = ['EMT', 'POLICE'];

    switch (overriddenStep) {
        case VerificationStepsEnum.success:
            mockResponse = mockSuccessResponse;
            logger.info('getOverriddenMock: mocking success response');
            break;
        case VerificationStepsEnum.docUpload:
            mockResponse = mockDocUploadVerificationResponse;
            logger.info('getOverriddenMock: mocking docUpload response');
            break;
        case VerificationStepsEnum.pending:
            mockResponse = mockPendingResponse;
            logger.info('getOverriddenMock: mocking pending response');
            break;
        case VerificationStepsEnum.docReviewLimitExceeded:
            mockResponse = mockDocReviewLimitExceededResponse;
            logger.info('getOverriddenMock: mocking docReviewLimitExceeded response');
            break;
        case VerificationStepsEnum.error:
            mockResponse = mockErrorResponse;
            logger.info('getOverriddenMock: mocking error response');
            if (getErrorIdOverride()) {
                mockResponse.errorIds = [getErrorIdOverride()];
            }
            break;
        case VerificationStepsEnum.collectStudentPersonalInfo:
            mockResponse = mockStudentPersonalInfoResponse;
            logger.info('getOverriddenMock: mocking mockStudentPersonalInfoResponse response');
            break;
        case VerificationStepsEnum.collectTeacherPersonalInfo:
            mockResponse = mockTeacherPersonalInfoResponse;
            logger.info('getOverriddenMock: mocking mockTeacherPersonalInfoResponse response');
            break;
        case VerificationStepsEnum.collectMilitaryStatus:
            mockResponse = mockMilitaryStatusResponse;
            logger.info('getOverriddenMock: mocking mockMilitaryStatusResponse response');
            break;
        case VerificationStepsEnum.collectActiveMilitaryPersonalInfo:
            mockResponse = { ...mockActiveMilitaryPersonalInfoResponse, availableStatuses: availableMilitaryStatuses };
            logger.info('getOverriddenMock: mocking mockActiveMilitaryPersonalInfoResponse response');
            break;
        case VerificationStepsEnum.collectInactiveMilitaryPersonalInfo:
            mockResponse = { ...mockInactiveMilitaryPersonalInfoResponse, availableStatuses: availableMilitaryStatuses };
            logger.info('getOverriddenMock: mocking mockInactiveMilitaryPersonalInfoResponse response');
            break;
        case VerificationStepsEnum.collectSeniorPersonalInfo:
            mockResponse = mockSeniorPersonalInfoResponse;
            logger.info('getOverriddenMock: mocking mockSeniorPersonalInfoResponse response');
            break;
        case VerificationStepsEnum.collectFirstResponderPersonalInfo:
            mockResponse = { ...mockFirstResponderPersonalInfoResponse, availableStatuses: availableFirstResponderStatuses };
            logger.info('getOverriddenMock: mocking mockFirstResponderPersonalInfoResponse response');
            break;
        // TODO medical
        case VerificationStepsEnum.collectEmployeePersonalInfo:
            mockResponse = mockEmploymentPersonalInfoResponse;
            logger.info('getOverriddenMock: mocking employmentPersonalInfoResponse');
            break;
        case VerificationStepsEnum.sso:
            mockResponse = mockSSOResponse;
            logger.info('getOverriddenMock: mocking mockSSOResponse response');
            break;
        default:
            mockResponse = null;
            logger.info(`
                getOverriddenMock: not mocking response.
                You may mock a step response by adding things like ?mockStep=success to your route.
                ${overriddenStep}
            `);
    }

    if (mockResponse !== null && serverResponse) {
        await serverResponse;
        mockResponse.segment = serverResponse.segment;
        mockResponse.subSegment = serverResponse.subSegment;
        if (getSubsegmentOverride()) {
            mockResponse.subSegment = getSubsegmentOverride();
        }
    }
    return Promise.resolve(mockResponse);
};
